const JAR_COUNT = 4;
const DEFAULT_JARS = ['B', 'B', 'B', 'B'];

const SELECTORS = {
  config: '[data-seal-fermentation-config]',
  sets: '[data-seal-fermentation-sets]',
  custom: '[data-seal-fermentation-custom]',
  modal: '[data-seal-fermentation-modal]',
  modalPanel: '[data-seal-fermentation-modal-panel]',
  status: '[data-seal-fermentation-status]',
};

const normalizeJars = (jars) => {
  const normalized = Array.isArray(jars)
    ? jars.map((jar) => String(jar || '').toUpperCase()).filter((jar) => jar === 'B' || jar === 'C')
    : [];

  while (normalized.length < JAR_COUNT) normalized.push('B');
  return normalized.slice(0, JAR_COUNT);
};

const normalizeState = (state = {}) => ({
  noJars: Boolean(state.noJars),
  jars: normalizeJars(state.jars || DEFAULT_JARS),
  tamper: Number(state.tamper ? 1 : 0),
});

const encodeSku = (state) => {
  const normalized = normalizeState(state);
  const tamperSuffix = normalized.tamper === 1 ? '_T1' : '';

  if (normalized.noJars) return `FERMSET${tamperSuffix}`;
  return `FERMSET_${JAR_COUNT}${normalized.jars.slice().sort().join('')}${tamperSuffix}`;
};

const decodeSku = (sku) => {
  const raw = String(sku || '').trim().toUpperCase();
  if (!raw) return null;

  if (raw === 'FERMSET' || raw === 'FERMSET_T1') {
    return normalizeState({
      noJars: true,
      jars: DEFAULT_JARS,
      tamper: raw.endsWith('_T1') ? 1 : 0,
    });
  }

  const matched = /^FERMSET_(\d+)([BC]+?)(?:_T1)?$/.exec(raw);
  if (!matched) return null;

  const quantity = Number(matched[1] || 0);
  const jars = String(matched[2] || '').split('');
  if (quantity !== JAR_COUNT || jars.length !== JAR_COUNT) return null;

  return normalizeState({
    noJars: false,
    jars,
    tamper: raw.endsWith('_T1') ? 1 : 0,
  });
};

const stateSignature = (state) => {
  const normalized = normalizeState(state);
  return `${normalized.noJars ? '1' : '0'}:${normalized.tamper}:${normalized.noJars ? '' : normalized.jars.slice().sort().join('')}`;
};

class SealFermentationVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.abortController = new AbortController();
    customElements.whenDefined('variant-picker').then(() => this.init());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.closeJarModal({ immediate: true });
    this.initialized = false;
  }

  reinitialize() {
    if (!this.isConnected) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.closeJarModal({ immediate: true });
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized || !this.isConnected) return;

    this.config = this.readConfig();
    this.ui = this.config.ui || {};
    this.assets = this.config.assets || {};
    this.fixedSets = Array.isArray(this.config.fixedSets) ? this.config.fixedSets : [];
    this.view = 'sets';
    this.selectedSetId = this.config.defaultSetId || this.fixedSets[0]?.id || '';
    this.state = normalizeState(this.fixedSets.find((set) => set.id === this.selectedSetId)?.state);
    this.currentJarIndex = 0;

    this.cacheElements();
    try {
      this.renderedVariantId = String(JSON.parse(this.nativePicker?.querySelector('script[type="application/json"]')?.textContent || 'null')?.id || '');
    } catch {
      this.renderedVariantId = '';
    }
    this.requestedVariantId = null;
    this.initFromCurrentVariant();
    this.bindEvents();
    this.render();
    this.syncNativeVariant({ announce: false });

    this.initialized = true;
  }

  readConfig() {
    const script = this.querySelector(SELECTORS.config);
    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[Seal Fermentation Picker] Invalid config JSON', error);
      return {};
    }
  }

  cacheElements() {
    this.nativePicker = this.querySelector('variant-picker');
    this.setsEl = this.querySelector(SELECTORS.sets);
    this.customEl = this.querySelector(SELECTORS.custom);
    this.modal = this.querySelector(SELECTORS.modal);
    this.modalPanel = this.querySelector(SELECTORS.modalPanel);
    this.statusEl = this.querySelector(SELECTORS.status);
  }

  bindEvents() {
    const { signal } = this.abortController;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('change', this.handleChange, { signal });
    document.addEventListener('keydown', this.handleKeydown, { signal });
    this.sectionRoot.addEventListener('variant:update', this.handleVariantUpdate, { signal });
  }

  get sectionRoot() {
    return this.closest('.shopify-section') || document;
  }

  get formScope() {
    return this.closest('#quick-add-modal-content') || this.sectionRoot;
  }

  handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId || '') !== this.dataset.productId) return;
    this.renderedVariantId = String(event.detail?.resource?.id || '');
    window.requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.cacheElements();
      // A response can finish just before another click. Keep the latest choice
      // and re-request it if the native picker was morphed to an older variant.
      if (this.requestedVariantId && this.requestedVariantId !== this.renderedVariantId) {
        const requestedInput = this.nativePicker?.querySelector(`input[data-variant-id="${this.requestedVariantId}"]`);
        if (requestedInput) this.selectNativeBySku(requestedInput.dataset.variantSku);
        return;
      }
      this.syncFromNative();
      this.render();
    });
  };

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const setButton = target.closest('[data-seal-fermentation-set]');
    if (setButton && this.contains(setButton)) {
      this.consumeClick(event);
      this.selectSet(setButton.dataset.sealFermentationSet || '');
      return;
    }

    const specialButton = target.closest('[data-seal-fermentation-special]');
    if (specialButton && this.contains(specialButton)) {
      this.consumeClick(event);
      this.selectSpecialOption(specialButton.dataset.sealFermentationSpecial || '');
      return;
    }

    const showCustom = target.closest('[data-seal-fermentation-show-custom]');
    if (showCustom && this.contains(showCustom)) {
      this.consumeClick(event);
      this.closeJarModal({ immediate: true });
      this.showCustom();
      return;
    }

    const showSets = target.closest('[data-seal-fermentation-show-sets]');
    if (showSets && this.contains(showSets)) {
      this.consumeClick(event);
      this.closeJarModal({ immediate: true });
      this.showSets();
      return;
    }

    const editJar = target.closest('[data-seal-fermentation-edit-jar]');
    if (editJar && this.contains(editJar)) {
      this.consumeClick(event);
      this.openJarModal(Number(editJar.dataset.sealFermentationEditJar));
      return;
    }

    const modalJarChoice = target.closest('[data-seal-fermentation-modal-jar-choice]');
    if (modalJarChoice && this.contains(modalJarChoice)) {
      this.consumeClick(event);
      this.chooseJarFromModal(String(modalJarChoice.dataset.sealFermentationModalJarChoice || '').toUpperCase());
      return;
    }

    const closeModal = target.closest('[data-seal-fermentation-close-modal]');
    if ((closeModal && this.contains(closeModal)) || target === this.modal) {
      this.consumeClick(event);
      this.closeJarModal();
      return;
    }

  };

  consumeClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.modal?.classList.contains('is-open')) {
      this.closeJarModal();
    }
  };

  handleChange = (event) => {
    const target = event.target instanceof HTMLInputElement ? event.target : null;
    if (!target) return;

    if (target.matches('[data-seal-fermentation-no-jars]')) {
      if (target.checked) {
        this.applyCustomState({ ...this.state, noJars: true });
        return;
      }

      this.applyCustomState(this.resolveStateWithJars(this.state) || { ...this.state, noJars: false });
      return;
    }

    if (target.matches('[data-seal-fermentation-tamper]')) {
      this.applyCustomState({ ...this.state, tamper: target.checked ? 1 : 0 });
    }
  };

  initFromCurrentVariant() {
    const currentSku = this.getSelectedNativeInput()?.dataset.variantSku || this.config.defaultSku || '';
    const matchingSet = this.fixedSets.find((set) => set.sku === currentSku);

    if (matchingSet) {
      this.view = 'sets';
      this.selectedSetId = matchingSet.id;
      this.state = normalizeState(matchingSet.state);
      return;
    }

    if (this.config.fixedSetsOnly) {
      const defaultSet = this.fixedSets.find((set) => set.id === this.config.defaultSetId) || this.fixedSets[0];
      if (defaultSet) {
        this.view = 'sets';
        this.selectedSetId = defaultSet.id;
        this.state = normalizeState(defaultSet.state);
      }
      return;
    }

    const decoded = decodeSku(currentSku);
    if (decoded) {
      this.view = decoded.noJars ? 'custom' : 'sets';
      this.selectedSetId = '';
      this.state = this.resolvePreferredState(decoded) || decoded;
      return;
    }

    const defaultSet = this.fixedSets.find((set) => set.id === this.selectedSetId) || this.fixedSets[0];
    if (defaultSet) {
      this.view = 'sets';
      this.selectedSetId = defaultSet.id;
      this.state = normalizeState(defaultSet.state);
    }
  }

  syncFromNative() {
    const currentSku = this.getSelectedNativeInput()?.dataset.variantSku || '';
    if (!currentSku) return;

    const matchingSet = this.fixedSets.find((set) => set.sku === currentSku);
    if (matchingSet) {
      this.view = 'sets';
      this.selectedSetId = matchingSet.id;
      this.state = normalizeState(matchingSet.state);
      return;
    }

    if (this.config.fixedSetsOnly) {
      this.initFromCurrentVariant();
      this.syncNativeVariant({ announce: false });
      return;
    }

    const decoded = decodeSku(currentSku);
    if (decoded) {
      const currentState = normalizeState(this.state);
      this.view = decoded.noJars ? 'custom' : 'sets';
      this.selectedSetId = '';
      this.state = stateSignature(currentState) === stateSignature(decoded)
        ? normalizeState({ ...decoded, jars: currentState.jars })
        : decoded;
    }
  }

  selectSet(setId) {
    const set = this.fixedSets.find((item) => item.id === setId);
    if (!set || this.isSkuUnavailable(set.sku)) return;

    this.view = 'sets';
    this.selectedSetId = set.id;
    this.state = normalizeState(set.state);
    this.render();
    this.selectNativeBySku(set.sku);
    this.announce(set.title || '');
  }

  selectSpecialOption(optionId) {
    const option = this.getSpecialOptions().find((item) => item.id === optionId);
    if (!option || option.unavailable) {
      this.announce(this.ui.soldOut || '');
      return;
    }

    this.view = 'custom';
    this.selectedSetId = '';
    this.state = normalizeState(option.state);
    this.render();
    this.selectNativeBySku(option.sku);
    this.announce('');
  }

  showCustom() {
    this.selectSpecialOption('no-jars');
  }

  showSets() {
    const targetSet = this.fixedSets.find((set) => set.id === 'compact')
      || this.fixedSets.find((set) => set.id === this.config.defaultSetId)
      || this.fixedSets[0];

    if (targetSet && !this.isSkuUnavailable(targetSet.sku)) {
      this.selectSet(targetSet.id);
      return;
    }

    this.view = 'sets';
    this.selectedSetId = '';
    this.render();
    if (targetSet) this.announce(this.ui.soldOut || '');
  }

  setJar(index, jar) {
    if (!Number.isInteger(index) || index < 0 || index >= JAR_COUNT) return;
    if (jar !== 'B' && jar !== 'C') return;
    if (this.isJarUnavailable(jar)) return;

    const jars = normalizeJars(this.state.jars);
    jars[index] = jar;
    this.applyCustomState({ ...this.state, noJars: false, jars });
  }

  openJarModal(index) {
    if (this.view !== 'custom' || this.state.noJars) return;
    if (!Number.isInteger(index) || index < 0 || index >= JAR_COUNT) return;

    this.currentJarIndex = index;
    this.render();
    this.cacheElements();
    this.modal?.classList.add('is-open');
    this.modal?.setAttribute('aria-hidden', 'false');
    this.modal?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-seal-fermentation-close-modal]')?.focus();
  }

  closeJarModal(options = {}) {
    const modals = new Set(this.querySelectorAll(SELECTORS.modal));
    const panels = new Set(this.querySelectorAll(SELECTORS.modalPanel));
    if (this.modal) modals.add(this.modal);
    if (this.modalPanel) panels.add(this.modalPanel);

    if (options.immediate) {
      panels.forEach((panel) => {
        panel.style.transition = 'none';
      });
    }

    modals.forEach((modal) => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('inert', '');
    });

    if (options.immediate) {
      window.requestAnimationFrame(() => {
        panels.forEach((panel) => {
          panel.style.removeProperty('transition');
        });
      });
    }

    this.restoreBodyScroll();
  }

  chooseJarFromModal(jar) {
    const index = this.currentJarIndex;
    this.closeJarModal({ immediate: true });
    this.setJar(index, jar);
  }

  restoreBodyScroll() {
    if (!document.querySelector('.seal-fermentation-picker__modal.is-open')) {
      document.body.style.overflow = '';
    }
  }

  applyCustomState(nextState) {
    const preferred = this.resolvePreferredState(nextState);
    if (!preferred) {
      this.announce(this.ui.soldOut || '');
      this.render();
      return;
    }

    this.view = 'custom';
    this.selectedSetId = '';
    this.state = preferred;
    this.render();
    this.syncNativeVariant();
  }

  render() {
    this.dataset.view = this.view;
    const showSets = this.view === 'sets';
    const showCustom = this.view === 'custom';
    const previousSetScrollLeft = this.querySelector('.seal-fermentation-picker__set-track')?.scrollLeft || 0;

    if (this.setsEl) {
      this.setsEl.hidden = !showSets;
      this.setsEl.toggleAttribute('inert', !showSets);
      this.setsEl.setAttribute('aria-hidden', String(!showSets));
      this.setsEl.innerHTML = showSets ? this.renderSets() : '';
      const track = this.setsEl.querySelector('.seal-fermentation-picker__set-track');
      if (showSets && track instanceof HTMLElement) track.scrollLeft = previousSetScrollLeft;
    }

    if (this.customEl) {
      this.customEl.hidden = !showCustom;
      this.customEl.toggleAttribute('inert', !showCustom);
      this.customEl.setAttribute('aria-hidden', String(!showCustom));
      this.customEl.innerHTML = showCustom ? this.renderCustom() : '';
    }

    this.cacheElements();
    if (!this.modal?.classList.contains('is-open')) this.restoreBodyScroll();
    if (showSets && this.selectedSetId) this.queueSetCentering(this.selectedSetId, { immediate: !this.initialized });
  }

  queueSetCentering(setId, options = {}) {
    if (!setId) return;
    window.requestAnimationFrame(() => this.centerSetInTrack(setId, options));
  }

  centerSetInTrack(setId, options = {}) {
    const track = this.querySelector('.seal-fermentation-picker__set-track');
    if (!(track instanceof HTMLElement)) return;

    const setButton = Array.from(track.querySelectorAll('[data-seal-fermentation-set]'))
      .find((button) => button instanceof HTMLElement && button.dataset.sealFermentationSet === setId);

    if (!(setButton instanceof HTMLElement)) return;

    const targetLeft = setButton.offsetLeft - ((track.clientWidth - setButton.offsetWidth) / 2);
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, targetLeft));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({
      left: nextScrollLeft,
      behavior: options.immediate || reducedMotion ? 'auto' : 'smooth',
    });
  }

  renderSets() {
    const currentState = normalizeState(this.state);
    const cards = this.fixedSets.map((set) => {
      const selected = this.view === 'sets' && !currentState.noJars && set.id === this.selectedSetId;
      const unavailable = this.isSkuUnavailable(set.sku);
      const includes = Array.isArray(set.includes) ? set.includes : [];

      return `
        <button
          type="button"
          class="seal-fermentation-picker__set${selected ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}"
          data-seal-fermentation-set="${this.escapeAttribute(set.id)}"
          ${unavailable ? 'aria-disabled="true"' : ''}
        >
          <span class="seal-fermentation-picker__set-media">
            ${set.image ? `<img src="${this.escapeAttribute(set.image)}" alt="" loading="lazy" decoding="async">` : ''}
            <span class="seal-fermentation-picker__set-badge">${this.escapeHtml(unavailable ? this.ui.soldOut : set.badge)}</span>
            ${selected ? `<span class="seal-fermentation-picker__set-selected" aria-label="${this.escapeAttribute(this.ui.selected || 'Selected')}">✓</span>` : ''}
          </span>
          <span class="seal-fermentation-picker__set-body">
            <span class="seal-fermentation-picker__set-title">${this.escapeHtml(set.title)}</span>
            <ul class="seal-fermentation-picker__set-includes">
              ${includes.map((item) => this.renderCheckLine(item)).join('')}
            </ul>
            <span class="seal-fermentation-picker__note"><strong>${this.escapeHtml(this.ui.perfectFor)}:</strong> ${this.escapeHtml(set.note)}</span>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="seal-fermentation-picker__set-track" role="radiogroup" aria-label="${this.escapeAttribute(this.ui.groupLabel)}">
        ${cards}
      </div>
      ${this.config.fixedSetsOnly ? '' : this.renderSpecialOptions()}
    `;
  }

  renderSpecialOptions() {
    const title = this.ui.specialButtonTitle || 'Weitere Optionen';

    return `
      <button type="button" class="seal-fermentation-picker__back seal-fermentation-picker__special" data-seal-fermentation-show-custom>
        <span class="seal-fermentation-picker__back-icon" aria-hidden="true">&gt;</span>
        <span>${this.escapeHtml(title)}</span>
      </button>
    `;
  }

  renderSpecialOption(option, currentState) {
    const selected = currentState.noJars && currentState.tamper === option.state.tamper;
    const variant = this.getVariantData(option.sku);

    return `
      <button
        type="button"
        class="seal-fermentation-picker__special-option${selected ? ' is-selected' : ''}${option.unavailable ? ' is-unavailable' : ''}"
        data-seal-fermentation-special="${this.escapeAttribute(option.id)}"
        aria-pressed="${selected ? 'true' : 'false'}"
        ${option.unavailable ? 'aria-disabled="true"' : ''}
      >
        <span class="seal-fermentation-picker__special-check" aria-hidden="true">${selected ? '✓' : ''}</span>
        <span class="seal-fermentation-picker__special-option-copy">
          <strong>${this.escapeHtml(option.title)}</strong>
          ${option.copy || option.excluded ? this.renderSpecialOptionIncludes(option.copy, option.excluded) : ''}
        </span>
        <span class="seal-fermentation-picker__special-price">${this.escapeHtml(option.unavailable ? this.ui.soldOut : variant?.formattedPrice || '')}</span>
      </button>
    `;
  }

  getSpecialOptions() {
    const noJars = normalizeState({ noJars: true, jars: DEFAULT_JARS, tamper: 0 });
    const noJarsTamper = normalizeState({ noJars: true, jars: DEFAULT_JARS, tamper: 1 });
    const options = [
      {
        id: 'no-jars',
        state: noJars,
        sku: encodeSku(noJars),
        title: this.ui.noJarsWithoutTamper || '',
        copy: this.ui.noJarsOptionCopy || '',
        excluded: [this.ui.noJarsExcluded || ''],
      },
      {
        id: 'no-jars-tamper',
        state: noJarsTamper,
        sku: encodeSku(noJarsTamper),
        title: this.ui.noJarsWithTamper || this.ui.noJarsTamper || '',
        copy: this.ui.noJarsTamperCopy || '',
        excluded: [this.ui.noJarsExcluded || ''],
      },
    ];

    return options.map((option) => ({
      ...option,
      unavailable: this.isSkuUnavailable(option.sku),
    }));
  }

  renderSpecialOptionIncludes(copy, excluded = []) {
    const parts = String(copy || '')
      .split('+')
      .map((part) => part.trim())
      .filter(Boolean);
    const excludedParts = Array.isArray(excluded) ? excluded.filter(Boolean) : [];

    if (!parts.length && !excludedParts.length) return '';

    return `
      <ul class="seal-fermentation-picker__special-option-includes">
        ${parts.map((part) => `
          <li>
            <span class="seal-fermentation-picker__check" aria-hidden="true">✓</span>
            <span>${this.escapeHtml(part)}</span>
          </li>
        `).join('')}
        ${excludedParts.map((part) => `
          <li class="is-negative">
            <span class="seal-fermentation-picker__check" aria-hidden="true">×</span>
            <span>${this.escapeHtml(part)}</span>
          </li>
        `).join('')}
      </ul>
    `;
  }

  renderCustom() {
    const state = normalizeState(this.state);
    const title = this.ui.specialTitle || '';
    const copy = this.ui.specialPageCopy || this.ui.specialCopy || '';
    const options = this.getSpecialOptions();
    const important = this.ui.important || '';
    const compatibility = this.ui.compatibility || '';

    return `
      <button type="button" class="seal-fermentation-picker__back" data-seal-fermentation-show-sets>
        <span class="seal-fermentation-picker__back-icon" aria-hidden="true">&lt;</span>
        <span>${this.escapeHtml(this.ui.backToSets)}</span>
      </button>
      <div class="seal-fermentation-picker__custom-shell">
        <div class="seal-fermentation-picker__custom-head">
          <span class="seal-fermentation-picker__custom-heading">
            <strong>${this.escapeHtml(title)}</strong>
            ${copy ? `<span>${this.escapeHtml(copy)}</span>` : ''}
          </span>
        </div>

        <div class="seal-fermentation-picker__compatibility-note" role="note">
          <strong>${this.escapeHtml(important)}</strong>
          <span>${this.escapeHtml(compatibility)}</span>
        </div>

        <div class="seal-fermentation-picker__special-options" role="radiogroup" aria-label="${this.escapeAttribute(this.ui.specialGroupLabel)}">
          ${options.map((option) => this.renderSpecialOption(option, state)).join('')}
        </div>
      </div>
    `;
  }

  renderJarOverview(state) {
    return `
      <div class="seal-fermentation-picker__jar-overview">
        ${state.jars.map((jar, index) => this.renderJarOverviewItem(jar, index)).join('')}
      </div>
    `;
  }

  renderJarOverviewItem(jar, index) {
    const title = `${this.ui.slotPrefix} ${index + 1}`;
    const label = jar === 'C' ? this.ui.jarCDescription : this.ui.jarBDescription;
    const image = this.assets.jarImages?.[jar] || '';

    return `
      <button
        type="button"
        class="seal-fermentation-picker__jar-item"
        data-seal-fermentation-edit-jar="${index}"
        aria-label="${this.escapeAttribute(`${title}: ${label}`)}"
      >
        <span class="seal-fermentation-picker__jar-thumb">
          ${image ? `<img src="${this.escapeAttribute(image)}" alt="" loading="lazy" decoding="async">` : ''}
        </span>
        <span class="seal-fermentation-picker__jar-copy">
          <span class="seal-fermentation-picker__jar-title">${this.escapeHtml(title)}</span>
          <span class="seal-fermentation-picker__jar-meta">${this.escapeHtml(label)}</span>
        </span>
        <span class="seal-fermentation-picker__jar-edit">${this.escapeHtml(this.ui.change || '')}</span>
      </button>
    `;
  }

  renderJarModal(state) {
    const currentJar = state.jars[this.currentJarIndex] || DEFAULT_JARS[this.currentJarIndex] || 'B';
    const jarNumber = String(this.currentJarIndex + 1);
    const title = (this.ui.chooseJar || `${this.ui.slotPrefix} __number__`)
      .replace('{number}', jarNumber)
      .replace('__number__', jarNumber);

    return `
      <div class="seal-fermentation-picker__modal" data-seal-fermentation-modal aria-hidden="true" inert>
        <div
          class="seal-fermentation-picker__modal-panel"
          data-seal-fermentation-modal-panel
          role="dialog"
          aria-modal="true"
          aria-label="${this.escapeAttribute(title)}"
        >
          <div class="seal-fermentation-picker__modal-header">
            <h3 class="seal-fermentation-picker__modal-title">${this.escapeHtml(title)}</h3>
            <button type="button" class="seal-fermentation-picker__modal-close" data-seal-fermentation-close-modal aria-label="${this.escapeAttribute(this.ui.close || '')}">
              <span aria-hidden="true"></span>
            </button>
          </div>
          <div class="seal-fermentation-picker__modal-body">
            <div class="seal-fermentation-picker__modal-grid">
              ${this.renderModalJarChoice('B', currentJar)}
              ${this.renderModalJarChoice('C', currentJar)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderModalJarChoice(choice, currentJar) {
    const selected = choice === currentJar;
    const unavailable = this.isJarUnavailable(choice);
    const label = choice === 'C' ? this.ui.jarC : this.ui.jarB;
    const description = choice === 'C' ? this.ui.jarCDescription : this.ui.jarBDescription;
    const image = this.assets.jarImages?.[choice] || '';

    return `
      <button
        type="button"
        class="seal-fermentation-picker__modal-jar${selected ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}"
        data-seal-fermentation-modal-jar-choice="${choice}"
        ${unavailable ? 'disabled aria-disabled="true"' : ''}
      >
        ${unavailable ? `<span class="seal-fermentation-picker__modal-badge">${this.escapeHtml(this.ui.soldOut || '')}</span>` : ''}
        <span class="seal-fermentation-picker__modal-jar-media">
          ${image ? `<img src="${this.escapeAttribute(image)}" alt="" loading="lazy" decoding="async">` : ''}
        </span>
        <span class="seal-fermentation-picker__modal-jar-copy">
          <span class="seal-fermentation-picker__modal-jar-title">${this.escapeHtml(label)}</span>
          <span class="seal-fermentation-picker__modal-jar-text">${this.escapeHtml(description)}</span>
        </span>
      </button>
    `;
  }

  renderSummaryLines(state) {
    const lines = [
      { text: this.ui.itemLids },
      { text: this.ui.itemWeights },
    ];

    if (state.noJars) {
      lines.push({ text: this.ui.noJars });
    } else {
      const countB = state.jars.filter((jar) => jar === 'B').length;
      const countC = state.jars.filter((jar) => jar === 'C').length;
      if (countB > 0) lines.push({ text: `${countB}x ${this.ui.jarBDescription}` });
      if (countC > 0) lines.push({ text: `${countC}x ${this.ui.jarCDescription}` });
    }

    lines.push({
      text: state.tamper ? this.ui.itemWithTamper : this.ui.itemWithoutTamper,
      negative: !state.tamper,
    });

    return lines.filter((line) => line.text).map((line) => this.renderCheckLine(line.text, line)).join('');
  }

  renderCheckLine(line, options = {}) {
    const negative = Boolean(options.negative) || this.isNegativeLine(line);

    return `
      <li${negative ? ' class="is-negative"' : ''}>
        <span class="seal-fermentation-picker__check" aria-hidden="true">${negative ? '×' : '✓'}</span>
        <span>${this.escapeHtml(line)}</span>
      </li>
    `;
  }

  isNegativeLine(line) {
    const value = String(line || '').trim().toLowerCase();
    const withoutTamper = String(this.ui.itemWithoutTamper || '').trim().toLowerCase();
    return Boolean(withoutTamper && value === withoutTamper);
  }

  syncNativeVariant(options = {}) {
    const currentState = normalizeState(this.state);
    const sku = currentState.noJars
      ? this.resolveVariantSkuForState(currentState)
      : this.view === 'sets'
      ? this.fixedSets.find((set) => set.id === this.selectedSetId)?.sku
      : this.resolveVariantSkuForState(this.state);

    if (!sku) return false;
    const selected = this.selectNativeBySku(sku);
    if (selected && options.announce !== false) this.announce(this.ui.summaryHeading || '');
    return selected;
  }

  selectNativeBySku(sku) {
    const input = this.getNativeInputBySku(sku);
    if (!input) return false;

    this.requestedVariantId = String(input.dataset.variantId || '');
    this.syncHiddenVariantInputs(input.dataset.variantId);
    this.updateUrl(input.dataset.variantId);

    // Checked inputs and URL update immediately; the rendered price only changes
    // after Horizon's response. A checked input alone does not confirm that price.
    if (input.checked && this.renderedVariantId === this.requestedVariantId) return true;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  syncHiddenVariantInputs(variantId) {
    if (!variantId) return;

    this.formScope.querySelectorAll('product-form-component input[name="id"]').forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateUrl(variantId) {
    if (!variantId || this.dataset.productUrl !== window.location.pathname) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  getSelectedNativeInput() {
    return this.nativePicker?.querySelector('input[data-variant-sku]:checked') || null;
  }

  getNativeInputBySku(sku) {
    const targetSku = String(sku || '').trim();
    if (!targetSku) return null;

    const inputs = Array.from(this.nativePicker?.querySelectorAll('input[data-variant-sku]') || []);
    return inputs.find((input) => String(input.dataset.variantSku || '').trim() === targetSku) || null;
  }

  getVariantData(sku) {
    return this.config.variants?.[sku] || null;
  }

  isSkuUnavailable(sku) {
    const variant = this.getVariantData(sku);
    return !variant || !variant.exists || !variant.available;
  }

  isJarUnavailable(jar) {
    const availability = this.config.jarAvailability?.[jar]?.available;
    if (availability !== false) return false;

    const other = jar === 'B' ? 'C' : 'B';
    const otherAvailability = this.config.jarAvailability?.[other]?.available;
    return otherAvailability !== false;
  }

  isStampferUnavailable() {
    return Boolean(this.config.stampfer?.exists && this.config.stampfer.available === false);
  }

  resolveVariantSkuForState(state) {
    const normalized = normalizeState(state);
    const direct = encodeSku(normalized);
    if (this.getVariantData(direct)) return direct;

    const signature = stateSignature(normalized);
    const matchingSet = this.fixedSets.find((set) => stateSignature(set.state) === signature && this.getVariantData(set.sku));
    return matchingSet?.sku || direct;
  }

  resolvePreferredState(state) {
    const normalized = normalizeState(state);
    const candidates = [normalized];

    if (!this.isStampferUnavailable()) {
      candidates.push({ ...normalized, tamper: normalized.tamper ? 0 : 1 });
    }

    for (const candidate of candidates) {
      if (!this.isStateBlockedByJarAvailability(candidate) && !this.isSkuUnavailable(this.resolveVariantSkuForState(candidate))) {
        return candidate;
      }
    }

    for (const candidate of candidates) {
      if (!this.isStateBlockedByJarAvailability(candidate) && this.getVariantData(this.resolveVariantSkuForState(candidate))) {
        return candidate;
      }
    }

    return null;
  }

  resolveStateWithJars(state) {
    const normalized = normalizeState({ ...state, noJars: false });
    const jarCandidates = [
      normalized.jars,
      DEFAULT_JARS,
      ['B', 'B', 'C', 'C'],
      ['C', 'C', 'C', 'C'],
      ['B', 'B', 'B', 'C'],
      ['B', 'C', 'C', 'C'],
    ];
    const signatures = new Set();

    for (const jars of jarCandidates) {
      const candidate = normalizeState({ ...normalized, jars });
      const signature = stateSignature(candidate);
      if (signatures.has(signature)) continue;
      signatures.add(signature);

      const preferred = this.resolvePreferredState(candidate);
      if (preferred && !preferred.noJars) return preferred;
    }

    return null;
  }

  isStateBlockedByJarAvailability(state) {
    const normalized = normalizeState(state);
    if (normalized.noJars) return false;
    return normalized.jars.some((jar) => this.isJarUnavailable(jar));
  }

  jarSummaryLabel(state) {
    const countB = state.jars.filter((jar) => jar === 'B').length;
    const countC = JAR_COUNT - countB;
    if (countB === JAR_COUNT) return `4x ${this.ui.jarBDescription}`;
    if (countC === JAR_COUNT) return `4x ${this.ui.jarCDescription}`;
    return `${countB}x ${this.ui.jarBDescription}, ${countC}x ${this.ui.jarCDescription}`;
  }

  announce(message) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message || '';
  }

  decodeHtmlEntities(value) {
    const text = String(value ?? '');
    if (!text.includes('&')) return text;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  escapeHtml(value) {
    return this.decodeHtmlEntities(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('seal-fermentation-variant-picker')) {
  customElements.define('seal-fermentation-variant-picker', SealFermentationVariantPicker);
}
